import { TimeField } from "@mui/x-date-pickers";
import clsx from "clsx";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";

import {
  getColorByOccupancy,
  IVehicleOccupancyControlBase,
} from "@ryde/controls";
import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  deepPurpleMui,
  greenMui,
  greyMui,
  orangeMui,
  redMui,
  rydeColors,
} from "@ryde/theme";

import { KanbanCardStyled } from "../../common";

export const KanbanItemStyled = styled(
  KanbanCardStyled,
).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item",
})`
  margin-bottom: 16px;
  margin-inline-start: 2px;
  margin-top: 3px;
`;

export const KanbanItemHeaderStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-header",
})`
  height: 18px;
  width: calc(100% - 24px);
  top: -24px;
  position: absolute;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: ${blueGreyMui._500};
  div.right {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-inline-start: auto;
  }
  button.dragButton {
    border: none;
    height: 14px;
  }
`;

export const KanbanItemLine2 = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-line2",
})`
  position: absolute;
  width: 100%;
  height: 35px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding-inline: 12px;
  border-bottom: 1px solid ${greyMui._300};

  .hub_icon_wrapper {
    display: inline-flex;
    align-items: center;
    width: 14px;
    height: 14px;

    .hub-icon {
      width: 14px;
      height: 14px;
    }
  }

  label {
    font-size: 18px;
    font-weight: 600;
  }
`;

export const KanbanItemLine3 = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-line3",
})`
  position: absolute;
  top: 35px;
  width: 100%;
  height: 35px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding-inline: 12px;
  font-size: 13px;
  color: ${blueGreyMui._500};
  span.course {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .kanban_course_name {
    max-width: 230px;
    line-height: 13px;
  }
`;

export const KanbanItemLineFrom = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-line-from",
})`
  position: absolute;
  top: 70px;
  width: 100%;
  height: 35px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding-inline: 12px;
  label {
    font-size: 13px;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const KanbanItemLineTo = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-line-to",
})`
  position: absolute;
  top: 105px;
  width: 100%;
  height: 35px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding-inline: 12px;
  label {
    font-size: 13px;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const KanbanItemLineMapPlace = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-line-map-place",
})`
  position: absolute;
  top: 144px;
  width: 100%;

  padding-inline: 12px;
  .kanban_areas_container {
    width: 100%;
    display: grid;
    grid-template-columns: 10px 1fr;
    align-items: center;
    gap: 10px;
    .kanban_areas {
      font-size: 12px;
      line-height: 13px;
    }
  }
`;

const lastChildLine = css`
  .KanbanFooterBlock:not(:last-child):after {
    content: "";
    border-inline-end: 1px solid ${blueGreyMui._500};
    height: 8px;
    margin-inline-start: 4px;
    display: inline-block;
  }
`;

export const KanbanItemFooterLine1 = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-footer-line1",
})`
  position: absolute;
  bottom: 45px;
  width: calc(100% - 24px);
  display: flex;
  align-items: center;
  height: 16px;
  font-size: 11px;
  font-weight: 400;

  ${lastChildLine}
`;

export const KanbanItemFooterLine2 = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-footer-line2",
})`
  position: absolute;
  bottom: 26px;
  width: calc(100% - 24px);
  display: flex;
  align-items: center;
  height: 16px;
  font-size: 11px;
  font-weight: 400;

  ${lastChildLine}
`;

export const KanbanItemFooterLine3 = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-footer-line3",
})`
  position: absolute;
  bottom: 7px;
  width: calc(100% - 24px);
  display: flex;
  align-items: center;
  height: 16px;
  font-size: 11px;
  font-weight: 400;

  ${lastChildLine}
`;

export const KanbanFooterBlock = styled.span.attrs<IStyledAttributes>(
  ({ className }) => ({
    [DATA_E2E_KEY]: "kanban-footer-block",
    className: clsx("KanbanFooterBlock", className),
  }),
)`
  font-weight: 400;
  margin-inline-end: 6px;
  color: black;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 12.65px;
  line-height: 12.65px;
  display: inline-flex;
  align-items: center;

  label {
    font-weight: 600;
    margin-inline-end: 5px;
    color: ${blueGreyMui._500};
  }

  .sparkle_svg {
    width: 12px;
    height: 12px;
    margin-bottom: -2px;
    margin-inline-end: 3px;
    color: ${deepPurpleMui.A700};
  }

  .airplane_svg {
    width: 12px;
    height: 12px;
    margin-bottom: -2px;
    margin-inline-end: 3px;
  }

  .journey_svg {
    width: 12px;
    height: auto;
    color: ${rydeColors.darkBlue};
  }

  .driver_phone_forwarded_icon {
    width: 12px;
    margin-bottom: -2px;
    height: auto;
    margin-inline-end: 3px;
    color: ${deepPurpleMui.A700};
    cursor: pointer;
  }

  /* Driver approval of the ride (RYDE-8099), shown just before the driver's name: a ticked green
     box once they approve, an empty one until then. Neither is rendered when no approval warning
     was ever raised for the ride. */
  .driver_approved_svg,
  .driver_not_approved_svg {
    width: 12px;
    height: auto;
    margin-bottom: -2px;
    margin-inline-end: 3px;
  }

  .driver_approved_svg {
    color: ${greenMui._700};
  }

  .driver_not_approved_svg {
    color: ${greyMui._500};
  }
`;

export const KanbanFooterPrice = styled.span.attrs<IStyledAttributes>({
  "data-e2e": "kanban-footer-price",
})`
  margin-inline-start: auto;
  color: ${redMui.A400};
`;

export const KanbanFooterPhone = styled.a.attrs<IStyledAttributes>({
  "data-e2e": "kanban-footer-phone",
})`
  margin-inline-start: auto;
  color: ${blueMui.A400};
  text-decoration: none;
`;

export const LineFooterDivider = styled.div`
  position: absolute;
  width: 100%;
  height: 0;
  bottom: 70px;
  border-top: 1px solid ${greyMui._300};
`;

export const OvalDirectionWrapper = styled.span`
  margin-inline-start: auto;
`;

export const KanbanItemPassengersGridWrapper = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "kanban-passenger-grid-wrapper",
  },
)`
  position: relative;
  top: 172px;
  width: 100%;
  height: 127px;
  padding-inline-start: 12px;
  padding-inline-end: 3px;
  margin-inline-end: 7px;
  overflow-x: hidden;
  overflow-y: auto;
`;

export const KanbanGridPassengerRowStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "kanban-passenger-row",
  },
)<{
  $isDragClone?: boolean;
}>`
  font-size: 12px;
  height: 25px;
  width: 100%;
  display: flex;
  align-items: center;
  background-color: ${rydeColors.white};
  gap: 7px;
  cursor: pointer;
  padding-inline-start: 3px;
  box-sizing: border-box;
  ${({ $isDragClone }) =>
    $isDragClone &&
    css`
      border: 3px solid ${rydeColors.dragBorderColor};
      box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.25);
      user-select: none;
    `}
  span.drag_grip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 10px;
    flex-shrink: 0;
    line-height: 1;
    cursor: pointer;
    touch-action: none;

    svg {
      pointer-events: none;
    }
  }

  span.drag_grip--placeholder {
    user-select: none;
    pointer-events: none;

    .drag_grip-placeholder-diamond {
      display: block;
      width: 5px;
      height: 5px;
      background-color: ${blueGreyMui._100};
      transform: rotate(45deg);
      border-radius: 1px;
    }
  }

  span.drag_grip--scope-restriction {
    pointer-events: auto;
    cursor: default;

    .drag_grip-scope-restriction-dot {
      display: block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: ${orangeMui._500};
    }
  }
  span.checkbox_user {
  }
  span.time {
    width: 27px;
    white-space: nowrap;
  }
  &.ampm span.time {
    width: 47px;
  }

  span.kanban-passenger-tooltip-trigger {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    flex: 1 1 0;
    overflow: hidden;
  }

  span.family_name_wrap {
    display: flex;
    align-items: center;
    gap: 3px;
    width: 78px;
    flex: 0 0 78px;
    overflow: hidden;

    .passenger-name-tooltip-wrap {
      display: flex;
      min-width: 0;
      flex: 1;
      overflow: hidden;
    }

    .passenger-name-tooltip-wrap > * {
      display: block;
      min-width: 0;
      flex: 1;
      max-width: 100%;
      overflow: hidden;
    }

    .passenger-name-tooltip-wrap .passenger-name-truncate-inner {
      display: block;
      min-width: 0;
      flex: 1;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .hub-ride-companion-mark {
      flex-shrink: 0;
      font-size: 10px;
      line-height: 1;
      color: ${orangeMui._800};
      cursor: default;
    }

    .ride-passenger-site-mismatch-mark {
      flex-shrink: 0;
      color: ${greyMui._400};
      font-size: 14px;
      font-weight: 500;
      line-height: 1;
      margin-inline-start: 4px;
    }
  }
  &.ampm span.family_name_wrap {
    width: 64px;
    flex: 0 0 64px;
  }

  span.family_name {
    display: block;
    min-width: 0;
    flex: 1;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.pointer {
      cursor: pointer;
    }
  }
  span.station {
    color: ${blueGreyMui._500};
    width: 73px;
    flex-shrink: 0;
  }
  &.ampm span.station {
    width: 68px;
  }
  span.boarding,
  [data-e2e="ride-boarding-indicator"] {
    width: 12px;
    flex-shrink: 0;
    margin-inline-start: -1px;
  }
  span.bin {
    width: 12px;
    display: inline-flex;
    align-items: center;
    visibility: hidden;
    line-height: 1;
    vertical-align: middle;
    margin-inline-start: 3px;
    margin-inline-end: 0;
    flex-shrink: 0;

    svg {
      cursor: pointer;
    }
  }

  span.chosen.bin {
    visibility: visible;
  }

  &:hover {
    background-color: ${greyMui._200};

    span.bin {
      visibility: visible;
    }
  }
`;

export const VerticalLine1 = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-item-vertical-line1",
})`
  border-inline-end: 1px solid ${blueGreyMui._300};
  width: 1px;
  height: 14px;
`;

export const DurationWrapper = styled.label<IVehicleOccupancyControlBase>`
  color: ${({ $busOccupancy }) => getColorByOccupancy($busOccupancy)};
`;

export const KanbanDateboxStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "kanban-datebox",
})`
  width: 64px;
  height: 24px;
  display: flex;
  align-items: center;
  border-radius: 3px;
  justify-content: space-evenly;
  span {
    font-size: 13px;
    font-weight: 400;
  }
`;

export const KanbanDateboxInStyled = styled(
  KanbanDateboxStyled,
).attrs<IStyledAttributes>({
  "data-e2e": "kanban-datebox-in",
})`
  background: ${rydeColors.blueTimeEditBg};
  border: 1px solid ${blueMui._300};

  &.ampm {
    width: 94px;
  }
`;

export const KanbanItemStyledTimeEditInputWrapper = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "kanban-time-edit-input",
  },
)`
  padding: 3px;
  gap: 4px;
  font-family: inherit;
  border-radius: 3px;
  font-size: 13px;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  color: ${rydeColors.black};
  text-align: center;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  cursor: pointer;
  background: ${rydeColors.blueTimeEditBg};
  border: 1px solid ${blueMui._300};

  &.isDropoff {
    background: ${rydeColors.redTimeEditBg};
    border: 1px solid ${redMui.A100};
  }

  &.error {
    border-color: ${redMui.A400};
    color: ${redMui.A400};
  }

  .startEndIcon {
    color: inherit;
    width: 14px;
    height: 14px;

    &.isEditMode {
      display: none;
    }
  }

  .calendarIcon {
    display: none;
    &.isEditMode {
      display: block;
      width: 14px;
      height: 14px;
      color: ${blueMui._300};

      &.isDropoff {
        color: ${redMui.A100};
      }
    }
  }
`;

export const KanbanTimeEditFieldStyled = styled(
  TimeField,
).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "kanban-time-edit-field",
})<{ $isEditMode: boolean; $isDropoff: boolean; $isAMPM: boolean }>`
  .MuiPickersOutlinedInput-root {
    padding: 0;

    &.Mui-error {
      background-color: ${rydeColors.redTimeEditBg};
    }
  }

  .MuiPickersSectionList-root {
    font-size: 13px;
    background-color: inherit;
    border: none;
    text-align: center;
    vertical-align: middle;
    padding: 0px;
    justify-content: center;
    width: ${({ $isAMPM }) => ($isAMPM ? "60px" : "40px")};

    ${({ $isEditMode, $isDropoff }) =>
      $isEditMode &&
      `
        border-radius: 3px;
        background: ${rydeColors.white};
        border: 1px solid ${$isDropoff ? redMui.A100 : blueMui._300};
      `}
  }
`;

export const EditKanbanIdLinkStyled = styled(Link).attrs<IStyledAttributes>({
  "data-e2e": "ride-id",
})`
  text-decoration: none;
  cursor: pointer;
  color: ${blueGreyMui._500};
  &:hover {
    text-decoration: underline;
  }
`;
